// ============================================================
// SignaturePad.js
// On-screen signature capture for material pickup, and the read-only and
// printed renderings of what it captured.
//
// WHY STROKES AND NOT AN IMAGE.
// A signature drawn on a tablet is a handful of polylines. Kept as polylines
// and serialised to SVG path data it is a few kilobytes of text that lives in
// the invoice document itself — no Firebase Storage, no upload, no second thing
// that can be missing when the document loads, and no bucket rules to get
// wrong. The same string draws on screen, on a printed invoice and on the
// shop's picking list, at any size, without blurring.
//
// A canvas toDataURL PNG of the same signature is 30–80 KB of base64 — an order
// of magnitude larger, resolution-locked, and it prints as a grey smudge on a
// laser printer. There is no version of this feature where the raster is the
// better store.
//
// SIZE, AND WHAT HAPPENS AT THE LIMIT.
// Points are decimated (nothing closer than MIN_STEP units to the last point is
// kept) and rounded to whole units. A fingertip's contact patch is about ten
// units wide in this space, so integer coordinates two units apart are already
// finer than the finger that drew them — the rounding costs nothing visible and
// saves a third of the string, because "L123 57" is eight characters where
// "L123.4 56.7" is twelve.
//
// That puts a normal signature at 1.5–4 KB and a slow, elaborate one with a
// long flourish at perhaps 8 KB. Firestore's limit is 1 MiB for the WHOLE
// document, so even the worst realistic signature is under 1% of it.
//
// MAX_SIGNATURE_CHARS is a hard stop at 40 KB anyway — roughly ten times the
// typical signature and still only 4% of the document budget. Past it, further
// points are dropped and the pad says so rather than silently growing. Nobody
// signs their name in 40 KB of path data; if that limit is ever actually hit it
// means something is drawing on the pad that is not a signature, and the right
// answer then is Firebase Storage and a URL in this field, not a bigger string.
// ============================================================

import React, { useState, useRef, useMemo, useCallback } from 'react';

// The coordinate space every signature is stored in. Fixed, so a signature
// captured on a phone and one captured on a tablet are the same shape, and a
// stored string can be re-rendered at any size for ever.
export const SIGNATURE_VIEWBOX = { w: 600, h: 200 };

// See the header note. A hard ceiling, not an expected value.
export const MAX_SIGNATURE_CHARS = 40000;

// Nothing closer than this to the previous point is recorded. A fingertip is
// about 10 units wide in this space, so 2 keeps every visible feature of a
// signature and throws away the sampling noise between them. It also bounds the
// worst case: a 240Hz stylus cannot produce more points than the length of the
// ink divided by this.
const MIN_STEP = 2;

// Whole units. See the size note in the header.
const unit = (n) => Math.round(n);

/** Strokes (arrays of [x, y]) to one SVG path string. */
export function strokesToPath(strokes) {
  return (strokes || [])
    .filter(s => s && s.length)
    .map(s => (s.length === 1
      // A single tap is a dot. Without the zero-length line it would serialise
      // to a moveto and draw nothing at all, and the person would have signed
      // something the paper does not show.
      ? `M${s[0][0]} ${s[0][1]}l0 0`
      : `M${s[0][0]} ${s[0][1]}` + s.slice(1).map(([x, y]) => `L${x} ${y}`).join('')))
    .join('');
}

/** Is there a signature on this document? */
export const hasSignature = (doc) =>
  typeof doc?.pickupSignature === 'string' && doc.pickupSignature.trim().length > 0;

/**
 * THE FOUR FIELDS, BUILT IN ONE PLACE.
 *
 * The shop tablet and the office both capture signatures, and they must write
 * the same four fields in the same shape — a screen inventing its own version
 * is how one of them ends up storing a name the other cannot read. This is the
 * only place the field names appear.
 *
 * It writes the four fields and NOTHING else: no pick status, no collected
 * items, and no inventory. Taking a signature records that a person received
 * goods; it does not decide whether the shop is finished, and it certainly does
 * not move stock. Returns null if there is not both a stroke and a name, so a
 * caller cannot write a half-signature.
 */
export function buildSignatureFields({ path, name, witnessedBy }) {
  const d = String(path || '').trim();
  const who = String(name || '').trim();
  if (!d || !who) return null;
  return {
    pickupSignature: d,
    pickupSignedName: who,
    pickupSignedAt: new Date().toISOString(),
    pickupWitnessedBy: witnessedBy || '',
  };
}

const fmtSignedAt = (v) => {
  if (!v) return '';
  const d = v?.toDate ? v.toDate() : new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

/**
 * A stored signature, drawn. Read-only by construction — it is a path and
 * nothing else, with no handlers of any kind.
 */
export function SignatureView({ path, height = 120, color = '#03045E', style }) {
  if (!path) return null;
  return (
    <svg
      viewBox={`0 0 ${SIGNATURE_VIEWBOX.w} ${SIGNATURE_VIEWBOX.h}`}
      width="100%"
      height={height}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Customer signature"
      style={{ display: 'block', maxWidth: '100%', ...style }}
    >
      <path
        d={path}
        fill="none"
        stroke={color}
        strokeWidth={3}
        strokeLinecap="round"
        strokeLinejoin="round"
        // The signature must survive a black-and-white printer, where a
        // coloured stroke can be dropped or washed out entirely.
        style={{ WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}
      />
    </svg>
  );
}

/**
 * THE PRINTED BLOCK, for the shop's picking list and the invoice's printed copy.
 *
 * Signed, it prints the signature, the name, the date and who took it.
 * UNSIGNED, IT STILL PRINTS A LINE. A paper pickup has to work: somebody walks
 * out to the yard with a sheet, the customer signs it with a pen, and the sheet
 * comes back. A blank "Received by" and date line costs nothing and is the
 * difference between the printout being a receipt and being a list.
 */
export function PrintSignatureBlock({ doc, title = 'Received by', className, lines = 'full' }) {
  const signed = hasSignature(doc);
  return (
    <div
      className={className}
      style={{
        marginTop: 18, paddingTop: 10, borderTop: '1px solid #9AA8BC',
        fontFamily: 'Manrope, sans-serif', breakInside: 'avoid', pageBreakInside: 'avoid',
      }}
    >
      <p style={{ margin: 0, fontSize: 11, fontWeight: 800, letterSpacing: 0.6, color: '#54657A' }}>
        {title.toUpperCase()}
      </p>

      {signed ? (
        <>
          <SignatureView path={doc.pickupSignature} height={90} style={{ margin: '2px 0' }} />
          <div style={{ borderTop: '1px solid #03045E', paddingTop: 4, display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#03045E' }}>
              {doc.pickupSignedName}
            </span>
            <span style={{ fontSize: 11, color: '#54657A' }}>
              {fmtSignedAt(doc.pickupSignedAt)}
              {doc.pickupWitnessedBy ? ` · witnessed by ${doc.pickupWitnessedBy}` : ''}
            </span>
          </div>
        </>
      ) : (
        // Ruled lines, not underscores: underscores break differently in every
        // font and leave a ragged line to write on.
        //
        // `lines="single"` is for a printed sheet that already has a second
        // signature column beside this one, where three sub-lines would crowd
        // the page; everywhere else gets the full set.
        <div style={{ display: 'flex', gap: 24, marginTop: 30, flexWrap: 'wrap' }}>
          <div style={{ flex: '2 1 240px', borderTop: '1px solid #03045E', paddingTop: 3 }}>
            <span style={{ fontSize: 10, color: '#54657A' }}>Signature</span>
          </div>
          {lines === 'full' && (
            <div style={{ flex: '2 1 200px', borderTop: '1px solid #03045E', paddingTop: 3 }}>
              <span style={{ fontSize: 10, color: '#54657A' }}>Print name</span>
            </div>
          )}
          <div style={{ flex: '1 1 120px', borderTop: '1px solid #03045E', paddingTop: 3 }}>
            <span style={{ fontSize: 10, color: '#54657A' }}>Date</span>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The pad.
 *
 * Pass `value` (a document carrying the four fields) with no onSave and it is a
 * read-only rendering. Pass onSave and it captures.
 *
 * POINTER, TOUCH AND MOUSE. Pointer Events already unify all three, so where
 * they exist they are the only handlers attached — attaching mouse handlers as
 * well would start a second stroke on every click, because a pointer event is
 * followed by a compatibility mouse event. Where they do not exist, touch and
 * mouse handlers are attached instead and the touch path calls preventDefault
 * so the compatibility mouse events never arrive.
 */
export default function SignaturePad({
  value,
  readOnly = false,
  onSave,
  onCancel,
  busy = false,
  defaultName = '',
  height = 200,
  compact = false,
}) {
  const [strokes, setStrokes] = useState([]);
  const [name, setName] = useState(defaultName);
  const [capped, setCapped] = useState(false);
  const drawing = useRef(false);
  const svgRef = useRef(null);

  const path = useMemo(() => strokesToPath(strokes), [strokes]);
  const hasInk = path.length > 0;
  const canSave = hasInk && name.trim().length > 0 && !busy;

  // Client coordinates into the fixed viewBox. The wrapper is given the same
  // aspect ratio as the viewBox, so this is a straight scale with no
  // letterboxing to correct for. The `|| w` fallbacks keep it from dividing by
  // zero before layout has happened.
  const toLocal = useCallback((clientX, clientY) => {
    const el = svgRef.current;
    const r = el && el.getBoundingClientRect ? el.getBoundingClientRect() : null;
    const w = (r && r.width) || SIGNATURE_VIEWBOX.w;
    const h = (r && r.height) || SIGNATURE_VIEWBOX.h;
    return [
      unit((clientX - (r ? r.left : 0)) * (SIGNATURE_VIEWBOX.w / w)),
      unit((clientY - (r ? r.top : 0)) * (SIGNATURE_VIEWBOX.h / h)),
    ];
  }, []);

  const start = useCallback((clientX, clientY) => {
    if (readOnly || busy) return;
    drawing.current = true;
    setStrokes(prev => [...prev, [toLocal(clientX, clientY)]]);
  }, [readOnly, busy, toLocal]);

  const extend = useCallback((clientX, clientY) => {
    if (!drawing.current) return;
    const pt = toLocal(clientX, clientY);
    setStrokes(prev => {
      if (!prev.length) return prev;
      const cur = prev[prev.length - 1];
      const last = cur[cur.length - 1];
      if (last && Math.abs(pt[0] - last[0]) < MIN_STEP && Math.abs(pt[1] - last[1]) < MIN_STEP) return prev;
      const next = [...prev.slice(0, -1), [...cur, pt]];
      // Refuse to grow past the ceiling rather than storing something the
      // document cannot afford. See the header note.
      if (strokesToPath(next).length > MAX_SIGNATURE_CHARS) { setCapped(true); return prev; }
      return next;
    });
  }, [toLocal]);

  const end = useCallback(() => { drawing.current = false; }, []);

  const clear = () => { setStrokes([]); setCapped(false); drawing.current = false; };

  const save = () => {
    if (!canSave || !onSave) return;
    onSave({ path, name: name.trim() });
  };

  const supportsPointer = typeof window !== 'undefined' && typeof window.PointerEvent === 'function';
  const handlers = readOnly ? {} : supportsPointer ? {
    onPointerDown: (e) => {
      if (e.currentTarget.setPointerCapture && e.pointerId != null) {
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* not fatal */ }
      }
      start(e.clientX, e.clientY);
    },
    onPointerMove: (e) => extend(e.clientX, e.clientY),
    onPointerUp: end,
    onPointerCancel: end,
    onPointerLeave: end,
  } : {
    onTouchStart: (e) => {
      // Stops the page scrolling under the finger AND stops the compatibility
      // mouse events that would otherwise draw the stroke a second time.
      if (e.preventDefault) e.preventDefault();
      const t = e.touches && e.touches[0];
      if (t) start(t.clientX, t.clientY);
    },
    onTouchMove: (e) => {
      if (e.preventDefault) e.preventDefault();
      const t = e.touches && e.touches[0];
      if (t) extend(t.clientX, t.clientY);
    },
    onTouchEnd: (e) => { if (e.preventDefault) e.preventDefault(); end(); },
    onTouchCancel: end,
    onMouseDown: (e) => start(e.clientX, e.clientY),
    onMouseMove: (e) => extend(e.clientX, e.clientY),
    onMouseUp: end,
    onMouseLeave: end,
  };

  // ── Read-only ──
  if (readOnly || (!onSave && value)) {
    if (!hasSignature(value)) return null;
    return (
      <div style={{ border: '2px solid #D3E0EC', borderRadius: 12, background: '#FFFFFF', padding: 12 }}>
        <SignatureView path={value.pickupSignature} height={compact ? 90 : 130} />
        <div style={{ borderTop: '1px solid #D3E0EC', marginTop: 6, paddingTop: 6 }}>
          <p style={{ margin: 0, fontSize: compact ? 14 : 17, fontWeight: 800, color: '#03045E' }}>
            {value.pickupSignedName}
          </p>
          <p style={{ margin: '2px 0 0 0', fontSize: compact ? 12 : 14, color: '#54657A' }}>
            {fmtSignedAt(value.pickupSignedAt)}
            {value.pickupWitnessedBy ? ` · witnessed by ${value.pickupWitnessedBy}` : ''}
          </p>
        </div>
      </div>
    );
  }

  // ── Capture ──
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <p style={{ margin: 0, fontSize: 15, color: '#54657A' }}>
        Sign below with a finger to confirm you received this material.
      </p>

      {/* The pad. aspect-ratio matches the viewBox exactly, so a point on the
          glass is the same point in the stored path with no correction.
          touchAction none is what stops the tablet panning the page instead of
          drawing — without it a signature scrolls the screen. */}
      <div style={{
        width: '100%', aspectRatio: `${SIGNATURE_VIEWBOX.w} / ${SIGNATURE_VIEWBOX.h}`,
        minHeight: height, background: '#FFFFFF',
        border: '2px solid #0077B6', borderRadius: 12, overflow: 'hidden', position: 'relative',
      }}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${SIGNATURE_VIEWBOX.w} ${SIGNATURE_VIEWBOX.h}`}
          preserveAspectRatio="none"
          width="100%"
          height="100%"
          aria-label="Signature pad"
          data-testid="signature-pad"
          style={{ display: 'block', touchAction: 'none', cursor: busy ? 'default' : 'crosshair' }}
          {...handlers}
        >
          {/* The line people expect to sign on. */}
          <line
            x1={40} y1={SIGNATURE_VIEWBOX.h - 42}
            x2={SIGNATURE_VIEWBOX.w - 40} y2={SIGNATURE_VIEWBOX.h - 42}
            stroke="#C7D6E5" strokeWidth={2}
          />
          <path
            d={path}
            fill="none"
            stroke="#03045E"
            strokeWidth={3}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {capped && (
        <p style={{ margin: 0, color: '#8C2020', fontSize: 14, fontWeight: 700 }}>
          That is as much as can be stored on this signature. Clear and sign again if it looks wrong.
        </p>
      )}

      {/* THE ONE INPUT ON THE SHOP SCREEN. A signature that cannot be read is
          worth very little without a name typed beside it, so the name is
          required, not optional. */}
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 15, fontWeight: 800, color: '#03045E' }}>
          Name of person signing
        </span>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={busy}
          placeholder="Full name"
          aria-label="Name of person signing"
          style={{
            width: '100%', minHeight: 52, padding: '0 14px',
            border: '2px solid #D3E0EC', borderRadius: 10,
            fontSize: 19, fontFamily: 'inherit', color: '#03045E', background: '#FFFFFF',
          }}
        />
      </label>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {/* CLEAR IS DELIBERATE AND ALWAYS AVAILABLE. A signature taken on glass
            with a finger goes wrong often, and a pad you cannot redo is a pad
            people sign badly and then argue about. */}
        <button type="button" onClick={clear} disabled={busy || !hasInk} style={btnClear(busy || !hasInk)}>
          ✕ Clear
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} disabled={busy} style={btnCancel}>
            Cancel
          </button>
        )}
        {/* Disabled until there is BOTH a stroke and a name — see buildSignatureFields,
            which refuses the same pair a second time so no caller can get past it. */}
        <button type="button" onClick={save} disabled={!canSave} style={btnSave(!canSave)}>
          ✓ Save signature
        </button>
      </div>
    </div>
  );
}

// Targets sized for the same gloved hand the rest of the shop screen is built
// for: Save at 52px, the two secondary controls at 48.
const btnSave = (disabled) => ({
  flex: '1 1 200px', minHeight: 52,
  background: disabled ? '#C7D6E5' : '#0F6E56',
  color: '#FFFFFF',
  border: `2px solid ${disabled ? '#C7D6E5' : '#0F6E56'}`,
  borderRadius: 10, fontSize: 19, fontWeight: 800,
  cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
});

const btnClear = (disabled) => ({
  minHeight: 48, padding: '0 20px',
  background: 'transparent', color: '#8C2020',
  border: '2px solid #E4B9B9', borderRadius: 10,
  fontSize: 17, fontWeight: 800,
  cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
  fontFamily: 'inherit',
});

const btnCancel = {
  minHeight: 48, padding: '0 20px',
  background: 'transparent', color: '#54657A',
  border: '2px solid #D3E0EC', borderRadius: 10,
  fontSize: 17, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
};
